import { useState, useRef, useCallback } from 'react';
import apiClient from '../apiClient';

/**
 * Simplified transcription hook with improved error handling
 * Removed excessive logging and debug code
 */
export const useTranscription = () => {
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState(null);
  const transcriptionRef = useRef('');

  const transcribeChunk = useCallback(async (blob, sessionId = null, endpoint = null, isFinal = false) => {
    if (!blob) {
      setError({ message: 'No audio data provided', code: 'INVALID_INPUT' });
      return;
    }

    setIsTranscribing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', blob, `chunk-${Date.now()}.wav`);
      // Markera sista chunken (avsluta) så backend kan ge finish-prioritet
      // (leeway-fönster + RunPod-förvärmning) endast då, inte för varje chunk.
      formData.append('is_final', isFinal ? 'true' : 'false');

      let response;
      if (sessionId && endpoint) {
        response = await apiClient.post(`${endpoint}/${sessionId}/upload_chunk`, formData, {
          timeout: 30000
        });
      } else {
        const fallbackEndpoint = endpoint || '/recorder/upload';
        response = await apiClient.post(fallbackEndpoint, formData, {
          timeout: 30000
        });
      }

      // Handle both string and object responses from backend
      let partialText = '';
      if (typeof response.data.transcription === 'string') {
        partialText = response.data.transcription;
      } else if (response.data.transcription && typeof response.data.transcription === 'object') {
        // Handle object response format from new API
        partialText = response.data.transcription.corrected_transcript || 
                     response.data.transcription.raw_transcript || '';
      } else {
        partialText = '';
      }
      
      if (partialText && partialText.trim && partialText.trim()) {
        transcriptionRef.current += (transcriptionRef.current ? ' ' : '') + partialText;
        setTranscription(transcriptionRef.current.trim());
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Transcription failed';
      setError({ 
        message: errorMessage, 
        code: err.response?.data?.error_code || 'TRANSCRIPTION_ERROR'
      });
      
      // Don't add error text to transcription, just log it
      console.error('Transcription chunk error:', err);
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  const resetTranscription = useCallback(() => {
    transcriptionRef.current = '';
    setTranscription('');
    setError(null);
  }, []);

  const setTranscriptionFromData = useCallback((text) => {
    transcriptionRef.current = text || '';
    setTranscription(text || '');
    setError(null);
  }, []);

  const appendText = useCallback((text) => {
    if (text?.trim()) {
      const newText = transcriptionRef.current + (transcriptionRef.current ? ' ' : '') + text;
      transcriptionRef.current = newText;
      setTranscription(newText.trim());
    }
  }, []);

  return {
    transcription,
    transcriptionRef,
    isTranscribing,
    error,
    transcribeChunk,
    resetTranscription,
    setTranscriptionFromData,
    appendText
  };
};